import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Radio,
  Shield,
  Server,
  Activity,
  AlertTriangle,
  Terminal,
  RefreshCw,
  Lock,
  Zap,
  History,
  FileText,
  Network,
  Globe,
  Copy,
  ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { AssetsApi, Port } from 'api-client';
import { useTeams } from '../../contexts/TeamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getApiConfiguration } from '../../api/utils';
import { TechnologyStack } from '../../components/assets/TechnologyStack';

interface port {
  port: Port | null;
  loading: boolean;
  error: string | null;
}

const initialport: port = {
  port: null,
  loading: true,
  error: null
};

const PortDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState<port>(initialport);
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  useEffect(() => {
    const fetchPortDetails = async () => {
      if (!id || !currentTeam || !token || !currentTeam.slug) return;

      try {
        const config = getApiConfiguration(token);
        const assetsApi = new AssetsApi(config);
        const portResponse = await assetsApi.portsRetrieve({ id, teamSlug: currentTeam.slug });
        console.log(portResponse)

        setData({
          port: portResponse,
          loading: false,
          error: null
        });
      } catch (error) {
        setData(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to fetch port details'
        }));
        console.error('Error fetching port details:', error);
      }
    };

    fetchPortDetails();
  }, [id, currentTeam, token]);

  if (data.loading) {
    return <div className="flex items-center justify-center h-screen">Loading port details...</div>;
  }

  if (data.error) {
    return <div className="flex items-center justify-center h-screen text-red-500">{data.error}</div>;
  }

  if (!data.port) {
    return <div className="flex items-center justify-center h-screen">Port not found</div>;
  }

  const handleAction = (action: string) => {
    console.log(`Performing ${action} on port ${id}`);
  };

  const parseJsonData = (data: string | null) => {
    try {
      return data ? JSON.parse(data) : null;
    } catch (error) {
      return null;
    }
  };

  const port = data.port;
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-lg bg-purple-500/10">
              <Radio className="h-6 w-6 text-purple-500" />
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-white">Port {port.number} ({port.protocol})</h1>
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <Badge variant={"status" in port && port?.status === 'open' ? 'open' : 'default'}>{port.state}</Badge>
                <span className="text-sm text-gray-400">Service: {port.service}</span>
                <span className="text-sm text-gray-400">Version: {port.version}</span>
              </div>
            </div>
          </div>
          {/* <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={() => handleAction('scan')}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Scan Port
            </Button>
            <Button variant="outline" onClick={() => handleAction('block')}>
              <Lock className="h-4 w-4 mr-2" />
              Block Port
            </Button>
          </div> */}
        </div>

        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="space-y-1">
            <div className="text-sm text-gray-400">Status</div>
            <div className="text-lg font-medium text-emerald-500">{port.state}</div>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-gray-400">Last Seen</div>
            <div className="text-sm text-white">{new Date(port.lastSeen).toLocaleString()}</div>
          </div>
          {JSON.parse(JSON.stringify(port.technologies))?.length > 0 && (
            <div className="bg-gray-800/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-400">Techologies</div>
                <Shield className="h-4 w-4 text-red-500" />
              </div>
              <TechnologyStack
                technologies={port.technologies}
                maxVisible={4}
              />
            </div>
          )}
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Main Content - 2 columns */}
        <div className="col-span-2 space-y-6">
          {/* Service Information */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Service Information</h2>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-400">Service Name</div>
                  <div className="text-sm text-white mt-1">{port?.service?.toUpperCase()}</div>
                </div>

                <div>

                </div>
              </div>
              <div className="space-y-4">
                <div>
                <div className="text-sm text-gray-400">Version</div>
                <div className="text-sm text-white mt-1">{port.version}</div>
                </div>

              </div>
            </div>
          </div>

          {/* Configuration */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Configuration</h2>
            <div className="space-y-6">
              <div>
                <div className="text-sm text-gray-400 mb-2">Service Banner</div>
                <div className="p-3 bg-gray-800/30 rounded-lg font-mono text-sm text-white break-words overflow-hidden">
                  {port.banner}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-400 mb-2">HTTP Headers</div>
                <div className="space-y-2">
                  {port.headers && typeof port.headers === 'object' ?
                    Object.entries(port.headers).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                        <span className="font-mono text-sm text-gray-300 mr-2">{key}:</span>
                        <span className="font-mono text-sm text-white break-words overflow-hidden">{String(value)}</span>
                      </div>
                    )) :
                    <div className="p-3 bg-gray-800/30 rounded-lg">
                      <span className="font-mono text-sm text-gray-300">No headers available</span>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          {/* History */}

        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Asset Information */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Asset Information</h2>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-400">Asset Type</div>
                <div className="text-sm font-medium text-white mt-1">
                  {parseJsonData(JSON.stringify(port.linkedObject))?.type || 'Unknown'}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Asset</div>
                <div className="text-sm font-medium text-white mt-1">
                  {parseJsonData(JSON.stringify(port.linkedObject))?.value || 'Unknown'}
                </div>
              </div>

            </div>
          </div>



          {/* Related Ports */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Screenshot</h2>
            <div className="relative aspect-video overflow-hidden">
        <img
          src={port?.screenshot || ''}
          alt={parseJsonData(JSON.stringify(port?.linkedObject))?.value || ''}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1629] to-transparent opacity-50" />

      </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortDetail;